import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useNavContext } from "../context/nav_context";
import {
  api_v3_get_tournament_by_id,
  api_v3_update_division_by_id,
  api_v3_publish_matches_by_league_day,
  api_v3_create_division,
} from "../utils/constants";
import { TfiAngleLeft } from "react-icons/tfi";
import { Loading } from "../components";
import PlayoffGenerationModal from "../components/admin/PlayoffGenerationModal";

const DIVISION_TEMPLATES = [
  { name: "Paladines", seq: 1, img: "paladines" },
  { name: "Campeones", seq: 2, img: "campeones" },
  { name: "Mandobles", seq: 3, img: "mandobles" },
  { name: "Espadas Largas", seq: 4, img: "espadas-largas" },
  { name: "Hombre de Armas", seq: 5, img: "hombres-de-armas" },
  { name: "Alabarderos", seq: 6, img: "alabarderos" },
  { name: "Milicias", seq: 7, img: "milicias" },
  { name: "Piqueros", seq: 8, img: "piqueros" },
  { name: "Lanceros", seq: 9, img: "lanceros" },
];

const AdminDivisions = () => {
  const { tournamentId } = useParams();
  const { user, accessToken } = useNavContext();
  const [divisionsInProgress, setDivisionsInProgress] = useState(false);
  const [divisionsError, setDivisionsError] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [isCreatingDivisions, setIsCreatingDivisions] = useState(false);
  const [selectedDivisionForPlayoffs, setSelectedDivisionForPlayoffs] = useState(null);
  const [pubState, setPubState] = useState({ inProgress: false, error: false, done: false });

  useEffect(() => {
    const loadDivisions = async () => {
      const url = api_v3_get_tournament_by_id(tournamentId);
      try {
        setDivisionsInProgress(true);
        const response = await axios.get(url);
        if (response.status === 200) {
          const divs = response.data.divisions.sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
          setDivisions(divs);
          setDivisionsInProgress(false);
          setDivisionsError(false);
          return true;
        } else {
          setDivisionsInProgress(false);
          setDivisionsError(true);
          return false;
        }
      } catch (error) {
        setDivisionsInProgress(false);
        setDivisionsError(true);
        return false;
      }
    };
    if (tournamentId) loadDivisions();
  }, [tournamentId]);

  useEffect(() => {
    if (pubState.done) setTimeout(() => setPubState((p) => ({ ...p, done: false })), 3000);
  }, [pubState.done]);

  const createDivisions = async (tournamentId) => {
    setIsCreatingDivisions(true);
    const baseUrl = "https://alexocampos.github.io/liga-espanhola-aoe2-static/divisions";
    try {
      for (const t of DIVISION_TEMPLATES) {
        await axios.post(api_v3_create_division(), {
          tournamentId,
          image: `${baseUrl}/${t.img}.png`,
          name: t.name,
          sequence: t.seq,
          groups: [{ name: "Grupo único", type: "REGULAR_LEAGUE" }],
        });
      }
      const response = await axios.get(api_v3_get_tournament_by_id(tournamentId));
      if (response.status === 200) {
        setDivisions(response.data.divisions.sort((a, b) => (a.sequence > b.sequence ? 1 : -1)));
      }
    } catch (error) {
      console.error("Error creating divisions:", error);
    } finally {
      setIsCreatingDivisions(false);
    }
  };

  const addGroupToDivision = async (divisionId) => {
    const updated = divisions.map((div) => {
      if (div.divisionId !== divisionId) return div;
      if (!div.groups || div.groups.length === 0) {
        div.groups = [{ name: "Grupo único", type: "REGULAR_LEAGUE" }];
      } else {
        if (div.groups.length === 1 && div.groups[0].name === "Grupo único") {
          div.groups[0].name = "Grupo A";
        }
        const nextLetter = String.fromCharCode(65 + div.groups.length);
        div.groups.push({ name: `Grupo ${nextLetter}`, type: "REGULAR_LEAGUE" });
      }
      return div;
    });
    setDivisions(updated);
    const divisionToUpdate = updated.find((d) => d.divisionId === divisionId);
    if (divisionToUpdate) {
      try { await axios.put(api_v3_update_division_by_id(divisionId), divisionToUpdate); }
      catch (e) { console.error("Error updating division:", e); }
    }
  };

  const removeGroupFromDivision = async (divisionId) => {
    const updated = divisions.map((div) => {
      if (div.divisionId !== divisionId) return div;
      if (div.groups && div.groups.length > 0) {
        div.groups.pop();
        if (div.groups.length === 1) div.groups[0].name = "Grupo único";
      }
      return div;
    });
    setDivisions(updated);
    const divisionToUpdate = updated.find((d) => d.divisionId === divisionId);
    if (divisionToUpdate) {
      try { await axios.put(api_v3_update_division_by_id(divisionId), divisionToUpdate); }
      catch (e) { console.error("Error updating division:", e); }
    }
  };

  const publishLeagueDay = async (leagueDay) => {
    try {
      setPubState({ inProgress: true, error: false, done: false });
      const access_token = accessToken || localStorage.getItem("access_token");
      if (!access_token) return false;
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      for (const div of divisions) {
        for (const group of div.groups || []) {
          const url = api_v3_publish_matches_by_league_day(div.tournamentId, div.divisionId, group.groupId, leagueDay);
          const response = await axios.put(url, config);
          if (response.status !== 200) {
            setPubState({ inProgress: false, error: true, done: false });
            return false;
          }
        }
      }
      setPubState({ inProgress: false, error: false, done: true });
    } catch (error) {
      setPubState({ inProgress: false, error: true, done: false });
      return false;
    }
  };

  if (!user) return <Canvas><EmptyState>Debes iniciar sesión para acceder a esta página.</EmptyState></Canvas>;
  if (!user.isAdmin) return <Canvas><EmptyState>Lo siento majete pero estás intentando acceder a donde no debes.</EmptyState></Canvas>;
  if (divisionsInProgress) return <Canvas><Loading /></Canvas>;
  if (divisionsError) return <Canvas><EmptyState>Ha ocurrido un error cargando las divisiones</EmptyState></Canvas>;

  if (!divisions || divisions.length === 0) {
    return (
      <Canvas>
        <Content>
          <PageTitle>ADMIN DIVISIONES</PageTitle>
          <EmptyDivisions>
            <EmptyTitle>¿No hay divisiones aún?</EmptyTitle>
            {isCreatingDivisions ? (
              <CreationBox>
                <Loading />
                <CreationText>Creando divisiones...</CreationText>
              </CreationBox>
            ) : (
              <CreateBtn onClick={() => createDivisions(tournamentId)}>CREAR DIVISIONES</CreateBtn>
            )}
          </EmptyDivisions>
        </Content>
      </Canvas>
    );
  }

  return (
    <Canvas>
      <Content>
        <HeaderRow>
          <PageTitle>ADMIN DIVISIONES</PageTitle>
          <BackLink to="/admin/">
            <TfiAngleLeft /> ADMIN HOME
          </BackLink>
        </HeaderRow>

        <Section>
          <SectionTitle>PUBLICAR JORNADA</SectionTitle>
          {pubState.error && <AlertError>Ha ocurrido un error publicando la jornada</AlertError>}
          {pubState.done && <AlertOk>Se ha publicado la jornada correctamente</AlertOk>}
          {pubState.inProgress ? (
            <Loading />
          ) : (
            <>
              <SectionDesc>Publica una jornada en todas las divisiones y grupos. ¡Cuidado! No tiene marcha atrás.</SectionDesc>
              <DayGrid>
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <DayBtn key={day} onClick={() => publishLeagueDay(day)}>
                    J{day}
                  </DayBtn>
                ))}
              </DayGrid>
            </>
          )}
        </Section>

        <DivisionsList>
          {divisions.map((division) => (
            <DivisionCard key={division.divisionId}>
              <DivIcon src={division.image} alt={division.name} />
              <DivName>{division.name}</DivName>
              <GroupList>
                {(division.groups || []).map((group) => (
                  <GroupPill
                    key={`${division.divisionId}-${group.groupId || group.name}`}
                    to={`/admin/tournament/${tournamentId}/division/${division.divisionId}/group/${group.groupId}`}
                  >
                    {group.name}
                  </GroupPill>
                ))}
              </GroupList>
              <DivActions>
                <IconBtn title="Añadir grupo" onClick={() => addGroupToDivision(division.divisionId)}>+</IconBtn>
                <IconBtn title="Eliminar grupo" onClick={() => removeGroupFromDivision(division.divisionId)}>−</IconBtn>
                <PlayoffBtn onClick={() => setSelectedDivisionForPlayoffs(division)}>PLAYOFFS</PlayoffBtn>
              </DivActions>
            </DivisionCard>
          ))}
        </DivisionsList>

        {selectedDivisionForPlayoffs && (
          <PlayoffGenerationModal
            division={selectedDivisionForPlayoffs}
            onClose={() => setSelectedDivisionForPlayoffs(null)}
            onGenerate={() => {}}
          />
        )}
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`
  background: var(--v-canvas);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  padding: 4rem 1.5rem;
`;

const Content = styled.div`
  width: 100%;
  max-width: 960px;
`;

const PageTitle = styled.h1`
  font-family: Anton, sans-serif;
  font-size: clamp(36px, 6vw, 56px);
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1;
  color: var(--v-fg);
  margin: 0;
`;

const EmptyState = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-fg-muted);
  text-align: center;
  padding: 4rem 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-mint);
  text-decoration: none;
  padding: 0.5rem 0.9rem;
  border: 1px solid rgba(60, 255, 208, 0.2);
  border-radius: 24px;
  transition: background 0.15s ease;
  &:hover { background: rgba(60, 255, 208, 0.04); }
`;

const Section = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-fg-muted);
  margin: 0 0 0.75rem;
`;

const SectionDesc = styled.p`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-fg-muted);
  margin: 0 0 1rem;
`;

const AlertError = styled.div`
  background: rgba(255, 59, 59, 0.1);
  border: 1px solid rgba(255, 59, 59, 0.2);
  color: #ff3b3b;
  font-family: var(--v-font-mono);
  font-size: 11px;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  margin-bottom: 0.75rem;
`;

const AlertOk = styled.div`
  background: rgba(60, 255, 208, 0.08);
  border: 1px solid rgba(60, 255, 208, 0.15);
  color: var(--v-mint);
  font-family: var(--v-font-mono);
  font-size: 11px;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  margin-bottom: 0.75rem;
`;

const DayGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const DayBtn = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: var(--v-fg);
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: var(--v-mint);
    background: rgba(60, 255, 208, 0.04);
  }
`;

const DivisionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DivisionCard = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  transition: border-color 0.15s ease;
  &:hover { border-color: rgba(60, 255, 208, 0.1); }
`;

const DivIcon = styled.img`
  width: 3rem;
  height: 3rem;
  object-fit: contain;
  flex-shrink: 0;
`;

const DivName = styled.span`
  font-family: var(--v-font-body);
  font-size: 16px;
  font-weight: 600;
  color: var(--v-fg);
  min-width: 8rem;
`;

const GroupList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const GroupPill = styled(Link)`
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--v-fg);
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: var(--v-mint);
    background: rgba(60, 255, 208, 0.04);
  }
`;

const DivActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: var(--v-fg-muted);
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover {
    border-color: var(--v-mint);
    color: var(--v-mint);
  }
`;

const PlayoffBtn = styled.button`
  padding: 0.35rem 0.85rem;
  border: 1px solid rgba(82, 0, 255, 0.3);
  background: transparent;
  color: var(--v-uv);
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(82, 0, 255, 0.06); }
`;

const EmptyDivisions = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const EmptyTitle = styled.h3`
  font-family: var(--v-font-body);
  font-size: 18px;
  font-weight: 600;
  color: var(--v-fg);
  margin: 0 0 1.5rem;
`;

const CreationBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const CreationText = styled.p`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-fg-muted);
  margin: 0;
`;

const CreateBtn = styled.button`
  background: var(--v-mint);
  border: none;
  color: #000;
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.7rem 2rem;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
`;

export default AdminDivisions;
